import React, { useState, useEffect } from "react";
// import "./joinRoom.css";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { UserAuth } from "../context/authContext";
import toast from "react-hot-toast";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import { FiLogIn, FiHash, FiUsers, FiUser } from "react-icons/fi";

const JoinRoom = () => {
  const [roomId, setRoomId] = useState(localStorage.getItem("roomId") || "");
  const [roomType, setRoomType] = useState("");
  const [team, setTeam] = useState(localStorage.getItem("team") || "");
  const [role, setRole] = useState(localStorage.getItem("role") || "");
  const [coinStake, setCoinStake] = useState("");
  const { userData } = UserAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);

  useEffect(() => {
    const storedRoomId = localStorage.getItem("roomId");
    if (storedRoomId) {
      const unsubscribe = db
        .collection("rooms")
        .doc(storedRoomId)
        .onSnapshot((doc) => {
          if (doc.exists) {
            setCurrentRoom({ id: doc.id, ...doc.data() });
          } else {
            setCurrentRoom(null);
          }
        });
      return () => unsubscribe();
    }
  }, []);

  const handleLeaveRoom = async () => {
    const storedRoomId = localStorage.getItem("roomId");
    const storedRole = localStorage.getItem("role");
    if (!storedRoomId) return;

    setIsLoading(true);
    try {
      await db
        .collection("rooms")
        .doc(storedRoomId)
        .update({
          [`${storedRole}s`]: firebase.firestore.FieldValue.arrayRemove(userData.publicKey),
        });
      localStorage.removeItem("roomId");
      localStorage.removeItem("role");
      localStorage.removeItem("team");
      setCurrentRoom(null);
      toast.success("Left room successfully.");
    } catch (error) {
      console.error("Error leaving room:", error);
      toast.error("Failed to leave room.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!roomId) return;
    setIsLoading(true);

    const unsubscribe = db
      .collection("rooms")
      .doc(roomId)
      .onSnapshot(
        (roomSnapshot) => {
          if (roomSnapshot.exists) {
            const data = roomSnapshot.data();
            setRoomType(data.roomType);
            setIsLoading(false);
          } else {
            setRoomType("");
            setIsLoading(false);
          }
        },
        (error) => {
          console.error("Error fetching room details:", error);
          toast.error("Error fetching room details.");
          setIsLoading(false);
        }
      );

    return () => unsubscribe();
  }, [roomId]);

  const handleJoinRoom = async () => {
    const existingRoomId = localStorage.getItem("roomId");
    if (existingRoomId) {
      toast.error("You are already in a room. Please leave it first.");
      return;
    }

    if (!roomId || !role) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsLoading(true);
    try {
      const roomRef = db.collection("rooms").doc(roomId);
      onAuthStateChanged(auth, (currentUser) => {
        if (currentUser?.email) {
          db.collection("users")
            .doc(currentUser.email)
            .update({
              stakeCoins: coinStake,
              balance: userData.balance - coinStake,
            });
        }
      });

      const roomSnapshot = await roomRef.get();

      if (!roomSnapshot.exists) {
        toast.error("Room not found. Please enter a valid room ID.");
        setIsLoading(false);
        return;
      }

      const data = roomSnapshot.data();
      const batch = db.batch();

      if (role === "miner" && roomType === "ASTRAPOS") {
        const newMiner = {
          publicKey: userData.publicKey,
          stake: Number(coinStake),
        };

        const stakers = data.stakers || [];
        if (stakers.length <= 2) {
          toast.error("Not enough miners yet. You will be added as a staker.");
          batch.update(roomRef, {
            stakers: firebase.firestore.FieldValue.arrayUnion(newMiner),
          });
        }
      } else if (roomType === "ASTRAHACK" && team) {
        batch.update(roomRef, {
          [`${team}_${role}s`]: firebase.firestore.FieldValue.arrayUnion(userData.publicKey),
        });
      } else {
        batch.update(roomRef, {
          [`${role}s`]: firebase.firestore.FieldValue.arrayUnion(userData.publicKey),
        });
      }

      await batch.commit();

      localStorage.setItem("roomId", roomId);
      localStorage.setItem("role", role);
      if (roomType === "ASTRAHACK") {
        localStorage.setItem("team", team);
        localStorage.setItem("role", team + "_" + role);
      }
      toast.success("Room joined successfully");
      if (roomType === "ASTRAHACK") {
        window.location.href = "/joinedhack";
      } else {
        window.location.href = "/joinedroom";
      }
    } catch (error) {
      console.error("Error joining room:", error);
      toast.error("Failed to join room. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col h-full">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <div className="p-2 bg-secondary/20 rounded-lg text-secondary">
          <FiLogIn />
        </div>
        Join Room
      </h2>

      {currentRoom && (
        <div className="bg-surfaceHighlight/50 border border-secondary/30 rounded-xl p-4 mb-6">
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-secondary text-xs font-bold uppercase tracking-wider mb-1">
                You are currently in:
              </p>
              <h3 className="text-lg font-bold text-white">
                {currentRoom.roomName || "Unnamed Room"}
              </h3>
              <p className="text-text-muted text-sm">Type: {currentRoom.roomType}</p>
              <p className="text-text-muted text-xs font-mono mt-1">ID: {currentRoom.id}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (currentRoom.roomType === "ASTRAHACK") {
                    window.location.href = "/joinedhack";
                  } else {
                    window.location.href = "/joinedroom";
                  }
                }}
                className="px-3 py-1.5 bg-secondary/10 text-secondary border border-secondary/30 rounded-lg text-sm hover:bg-secondary hover:text-white transition-all font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                onClick={handleLeaveRoom}
                disabled={isLoading}
                className="px-3 py-1.5 bg-red-500/10 text-red-500 border border-red-500/30 rounded-lg text-sm hover:bg-red-500 hover:text-white transition-all font-semibold"
              >
                {isLoading ? "Leaving..." : "Leave Room"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4 flex-1">
        <div>
          <label className="block text-text-muted text-sm font-medium mb-2">Room ID</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
              <FiHash />
            </div>
            <input
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-surfaceHighlight border border-border rounded-xl text-white placeholder-text-muted focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary transition-colors"
              placeholder="Enter Room ID"
              disabled={isLoading}
            />
          </div>
        </div>

        {roomType === "ASTRAHACK" && (
          <div>
            <label className="block text-text-muted text-sm font-medium mb-2">Team</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
                <FiUsers />
              </div>
              <select
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-surfaceHighlight border border-border rounded-xl text-white focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary appearance-none transition-colors"
                disabled={isLoading}
              >
                <option value="">Select Team</option>
                <option value="red">Red Team (Hacker)</option>
                <option value="blue">Blue Team (Normal)</option>
              </select>
            </div>
          </div>
        )}

        {roomType && (
          <>
            <div>
              <label className="block text-text-muted text-sm font-medium mb-2">Role</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-muted">
                  <FiUser />
                </div>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surfaceHighlight border border-border rounded-xl text-white focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary appearance-none transition-colors"
                  disabled={isLoading}
                >
                  <option value="">Select Role</option>
                  <option value="miner">{roomType === "ASTRAPOS" ? "Validator" : "Miner"}</option>
                  <option value="participant">Participant</option>
                </select>
              </div>
            </div>

            {roomType === "ASTRAPOS" && role === "miner" && (
              <div>
                <label className="block text-text-muted text-sm font-medium mb-2">Coin Stake</label>
                <input
                  type="number"
                  value={coinStake}
                  onChange={(e) => setCoinStake(e.target.value)}
                  className="w-full px-4 py-3 bg-surfaceHighlight border border-border rounded-xl text-white placeholder-text-muted focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary transition-colors"
                  disabled={isLoading}
                />
              </div>
            )}
          </>
        )}

        <button
          onClick={handleJoinRoom}
          disabled={
            isLoading ||
            !roomId ||
            !role ||
            (roomType === "ASTRAPOS" && role === "miner" && !coinStake)
          }
          className={`w-full mt-4 py-3 rounded-xl font-semibold text-white transition-all shadow-lg ${
            isLoading ||
            !roomId ||
            !role ||
            (roomType === "ASTRAPOS" && role === "miner" && !coinStake)
              ? "bg-surfaceHighlight text-text-muted cursor-not-allowed"
              : "bg-gradient-to-r from-secondary to-blue-500 hover:shadow-secondary/50"
          }`}
        >
          {isLoading ? "Joining..." : "Join Room"}
        </button>
      </div>
    </div>
  );
};

export default JoinRoom;
